import { useTheme } from '@emotion/react';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ACHIEVEMENT_FALLBACK_TYPES,
  type ACHIEVEMENT_TIERS,
  type ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';
import { Achievement, Typography } from '@didaskill/ui/design-system';
import { achievementMessages } from '@didaskill/ui/intl';

import * as styles from '../AchievementsDrawer.styles';
import { TIER_STARS_COLOR_GROUP_MAP } from '../constants';
import { StarsOrnament } from '../stars-ornament/StarsOrnament';
import messages from './DisplayedAchievement.messages';

type DisplayedAchievementProps = {
  achievement: {
    type: ACHIEVEMENT_TYPES | ACHIEVEMENT_FALLBACK_TYPES;
    tier: ACHIEVEMENT_TIERS;
  };
};

const DisplayedAchievementBase = ({ achievement }: DisplayedAchievementProps) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <div css={styles.achievementsDrawerBody}>
      <div css={styles.achievementWithStars}>
        <div css={styles.stars}>
          <StarsOrnament colorGroup={TIER_STARS_COLOR_GROUP_MAP(theme)[achievement.tier]} />
        </div>

        <Achievement
          size={Achievement.SIZES.GIANT}
          type={achievement.type}
          tier={achievement.tier}
        />
      </div>

      <div css={styles.content}>
        <Typography variant={Typography.VARIANTS.HEADING_3}>
          <FormattedMessage
            {...achievementMessages[achievement.type][achievement.tier].CATCH_PHRASE}
          />
        </Typography>

        <Typography variant={Typography.VARIANTS.BODY_LARGE_REGULAR}>
          <FormattedMessage
            {...messages[
              achievement.type === ACHIEVEMENT_FALLBACK_TYPES.DEFAULT
                ? 'ACHIEVEMENT_UNLOCKED_DEFAULT'
                : 'ACHIEVEMENT_UNLOCKED'
            ]}
            values={{
              p: (chunks) => (
                <Typography variant={Typography.VARIANTS.BODY_LARGE_BOLD}>{chunks}</Typography>
              ),
              achievementTitle: intl.formatMessage(
                achievementMessages[achievement.type][achievement.tier].TITLE,
              ),
            }}
          />
        </Typography>
      </div>
    </div>
  );
};

DisplayedAchievementBase.displayName = 'DisplayedAchievement';

export const DisplayedAchievement = memo(DisplayedAchievementBase);
